import React from "react";

/* DealScoreSparkline
 *   Compact inline SVG sparkline (no third-party charting dep).
 *   Props:
 *     - points  : array of {score, captured_at} (or {capped_score, calculated_at}) — oldest→newest
 *     - width   : px (default 90)
 *     - height  : px (default 28)
 *     - stroke  : override stroke colour
 *     - testid  : data-testid override
 */
export default function DealScoreSparkline({
  points = [],
  width = 90,
  height = 28,
  stroke,
  testid = "deal-score-sparkline",
}) {
  const values = (points || [])
    .map((p) => {
      const v = p?.score
        ?? p?.capped_score
        ?? p?.value
        ?? null;
      return typeof v === "number" ? v : null;
    })
    .filter((v) => v !== null);

  if (values.length < 2) {
    return (
      <div
        data-testid={`${testid}-empty`}
        className="text-[10px] text-slate-400 italic"
        style={{ width, height, lineHeight: `${height}px` }}
      >
        no history
      </div>
    );
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pad = 2;

  const stepX = values.length > 1
    ? (width - pad * 2) / (values.length - 1)
    : 0;

  const coords = values.map((v, i) => {
    const x = pad + i * stepX;
    const y = height - pad - ((v - min) / range) * (height - pad * 2);
    return [x, y];
  });

  const path = coords
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" ");

  const areaPath = `${path} L${coords[coords.length - 1][0].toFixed(1)},${height - pad}`
                    + ` L${coords[0][0].toFixed(1)},${height - pad} Z`;

  const first = values[0];
  const last = values[values.length - 1];
  const trending = last === first
    ? "flat"
    : last > first ? "up" : "down";

  const strokeCls = stroke || (
    trending === "up"   ? "#10b981" :
    trending === "down" ? "#ef4444" :
                          "#94a3b8"
  );
  const fillCls = trending === "up"   ? "rgba(16, 185, 129, 0.12)"
               : trending === "down" ? "rgba(239, 68, 68, 0.10)"
               :                       "rgba(148, 163, 184, 0.10)";

  return (
    <svg
      data-testid={testid}
      data-trending={trending}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
      role="img"
      aria-label={`Deal score trend ${trending}, ${values.length} points, latest ${last}`}
    >
      <path d={areaPath} fill={fillCls} />
      <path d={path} fill="none" stroke={strokeCls} strokeWidth={1.5}
             strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={coords[coords.length - 1][0]}
               cy={coords[coords.length - 1][1]}
               r={2.2} fill={strokeCls} />
    </svg>
  );
}
